import React, { useState, useEffect } from 'react';
import { Modal, Spin, Empty, Card, Image, Button, Tag, Divider, message, Pagination, Popconfirm, Tooltip } from 'antd';
import { 
  CloseOutlined, 
  FileTextOutlined, 
  CheckCircleOutlined,
  FileImageOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import adminRequest from '../../utils/adminRequest';
import AddQuestionsToSection from './AddQuestionsToSection';
import EditQuestionModal from './EditQuestionModal';
const CustomSectionQuestionsViewer = ({ 
  visible, 
  onClose, 
  sectionId, 
  sectionName,
  onQuestionsUpdated 
}) => {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  useEffect(() => {
    if (visible && sectionId) {
      setPage(1); // Reset to page 1 when opening
      fetchQuestions(1);
    }
  }, [visible, sectionId]);

  const fetchQuestions = async (pageNo = 1) => {
    setLoading(true);
    try {
      const response = await adminRequest.get(
        `/assessment/getQuestionsForCustomSection?section_id=${sectionId}&pageNo=${pageNo}&pageLimit=${pageSize}`
      );
      
      console.log('✅ Questions fetched:', response.data);
      if (response.data && response.data.questions) {
        setQuestions(response.data.questions || []);
        const newTotal = response.data.total || 0;
        setTotal(newTotal);
        // Notify parent of update
        if (onQuestionsUpdated) {
          onQuestionsUpdated(newTotal);
        }
      } else if (Array.isArray(response.data)) {
         // Fallback for backward compatibility
        setQuestions(response.data);
        setTotal(response.data.length);
      } else {
        setQuestions([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('❌ Failed to fetch questions:', error);
      message.error('Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = (question) => {
    setSelectedQuestion(question);
    setIsEditModalVisible(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    setDeleteLoading(questionId);
    try {
      const response = await adminRequest.delete(
        '/assessment/deleteCustomAssessmentQuestion',
        { data: { question_id: questionId } }
      );
      
      if (response.data && response.data.message) {
        message.success(response.data.message || 'Question deleted successfully');
        
        // Check if we need to go to previous page
        const remainingQuestions = questions.length - 1;
        if (remainingQuestions === 0 && page > 1) {
          setPage(page - 1);
          fetchQuestions(page - 1);
        } else {
          fetchQuestions(page);
        }
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      message.error(error.response?.data?.message || 'Failed to delete question');
    } finally {
      setDeleteLoading(null);
    }
  };

  const renderOption = (option, index, isCorrect) => {
    return (
      <div
        key={option.id}
        style={{
          padding: 12,
          marginBottom: 8,
          background: isCorrect ? '#f6ffed' : '#fafafa',
          border: isCorrect ? '2px solid #52c41a' : '1px solid #e8e8e8',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12
        }}
      >
        {/* Option Letter */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: isCorrect ? '#52c41a' : '#d9d9d9',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: 14,
            flexShrink: 0
          }}
        >
          {String.fromCharCode(65 + index)}
        </div>

        {/* Option Content */}
        <div style={{ flex: 1 }}>
          {/* Option Text */}
          <div style={{ 
            fontSize: 14, 
            color: '#262626',
            marginBottom: option.imageUrl ? 8 : 0
          }}>
            {option.optionText}
          </div>

          {/* Option Image */}
          {option.imageUrl && (
            <Image
              src={option.imageUrl}
              alt={`Option ${String.fromCharCode(65 + index)}`}
              style={{ 
                maxWidth: 200,
                borderRadius: 4,
                border: '1px solid #e8e8e8'
              }}
              preview={{
                mask: <FileImageOutlined style={{ fontSize: 20 }} />
              }}
            />
          )}
        </div>

        {/* Correct Indicator */}
        {isCorrect && (
          <CheckCircleOutlined 
            style={{ 
              fontSize: 20, 
              color: '#52c41a',
              flexShrink: 0
            }} 
          />
        )}
      </div>
    );
  };

  return (
    <>
<Modal
  title={
    <div style={{ 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FileTextOutlined style={{ fontSize: 18, color: '#1890ff' }} />
        <span style={{ fontSize: 16, fontWeight: 600 }}>
          {sectionName} - Questions
        </span>
        <Tag color="blue">{total} Questions</Tag>
      </div>
      
      <Button 
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setIsAddModalVisible(true)}
        size="small"
      >
        Add Questions
      </Button>
    </div>
  }
  open={visible}
  onCancel={onClose}
  width={900}
  footer={null}
  destroyOnClose
  styles={{
    body: {
      maxHeight: '70vh',
      overflowY: 'auto',
      padding: '24px'
    }
  }}
>
      {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 60 
        }}>
          <Spin size="large" tip="Loading questions..." />
        </div>
      ) : questions.length === 0 ? (
        <Empty 
          description="No questions found in this section"
          style={{ padding: 60 }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {questions.map((question, qIndex) => (
            <Card
              key={question.question_id}
              style={{
                borderRadius: 8,
                border: '1px solid #e8e8e8'
              }}
              bodyStyle={{ padding: 20 }}
            >
              {/* Question Number Header */}
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: '1px solid #f0f0f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: '#1890ff',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: 14
                  }}>
                    {qIndex + 1}
                  </div>
                  <span style={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#1890ff' 
                  }}>
                    Question {qIndex + 1}
                  </span>
                </div>
                
                {/* Edit and Delete Buttons */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Tooltip title="Edit Question">
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditQuestion(question)}
                      style={{ color: '#1890ff' }}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="Delete Question"
                    description="Are you sure you want to delete this question?"
                    onConfirm={() => handleDeleteQuestion(question.question_id)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                  >
                    <Tooltip title="Delete Question">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deleteLoading === question.question_id}
                      />
                    </Tooltip>
                  </Popconfirm>
                </div>
              </div>

              {/* Question Text */}
              <div style={{ 
                fontSize: 15,
                fontWeight: 500,
                color: '#262626',
                lineHeight: 1.6,
                marginBottom: question.question_image_url ? 16 : 20
              }}>
                {question.question_text}
              </div>

              {/* Question Image */}
              {question.question_image_url && (
                <div style={{ marginBottom: 20 }}>
                  <Image
                    src={question.question_image_url}
                    alt="Question"
                    style={{ 
                      maxWidth: '100%',
                      borderRadius: 8,
                      border: '1px solid #e8e8e8'
                    }}
                    preview={{
                      mask: (
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          gap: 4
                        }}>
                          <FileImageOutlined style={{ fontSize: 24 }} />
                          <span style={{ fontSize: 12 }}>Click to view</span>
                        </div>
                      )
                    }}
                  />
                </div>
              )}

              {/* Options Label */}
              <div style={{ 
                fontSize: 13,
                fontWeight: 600,
                color: '#8c8c8c',
                marginBottom: 12,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Answer Options
              </div>

              {/* Options List */}
              <div>
                {question.options.map((option, optIndex) => {
                  const isCorrect = option.optionValue > 0;
                  return renderOption(option, optIndex, isCorrect);
                })}
              </div>

              {/* Correct Answer Summary */}
              {question.correctOption && question.correctOption.length > 0 && (
                <div style={{ 
                  marginTop: 16,
                  padding: 12,
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                  <span style={{ fontSize: 13, color: '#389e0d', fontWeight: 500 }}>
                    Correct Answer: {question.correctOption.map(opt => 
                      opt.optionText
                    ).join(', ')}
                  </span>
                </div>
              )}
            </Card>

          ))}
          
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={(newPage) => {
                setPage(newPage);
                fetchQuestions(newPage);
              }}
              showSizeChanger={false}
            />
          </div>
        </div>
      )}
      </Modal>

{/* 👉 ADD HERE - Line 287 */}
<AddQuestionsToSection
  visible={isAddModalVisible}
  onClose={() => setIsAddModalVisible(false)}
  sectionId={sectionId}
  sectionName={sectionName}
  onSuccess={() => {
    setIsAddModalVisible(false);
    fetchQuestions(page); // Refresh current page
  }}
/>

{/* Edit Question Modal */}
<EditQuestionModal
  visible={isEditModalVisible}
  onClose={() => {
    setIsEditModalVisible(false);
    setSelectedQuestion(null);
  }}
  question={selectedQuestion}
  onSuccess={() => {
    setIsEditModalVisible(false);
    setSelectedQuestion(null);
    fetchQuestions(page); // Refresh current page
  }}
/>
</>
);
};

export default CustomSectionQuestionsViewer;